import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { onboardingApi } from "../../api/onboardingApi";
import { useToast } from "../../ui/toast/ToastProvider";
import "./onboarding.css";

const LOCAL_DISMISS_PREFIX = "bs:onboarding:dismissed:";
const TOUR_STATE_KEY = "bs:onboarding:active-tour";
const TOUR_STATE_EVENT = "bs:onboarding:active-tour-change";

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function clampProgress(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}


function readTourStateLocal() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(TOUR_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function notifyTourStateChange() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(TOUR_STATE_EVENT));
  } catch {
    try {
      const event = document.createEvent("Event");
      event.initEvent(TOUR_STATE_EVENT, true, true);
      window.dispatchEvent(event);
    } catch {}
  }
}

function writeTourStateLocal(nextState) {
  if (typeof window === "undefined") return null;
  const normalized = nextState && typeof nextState === "object" ? nextState : {};
  try {
    window.localStorage.setItem(TOUR_STATE_KEY, JSON.stringify({
      ...normalized,
      updatedAt: new Date().toISOString(),
    }));
    notifyTourStateChange();
    return normalized;
  } catch {
    return null;
  }
}

function stopTourStateLocal() {
  const current = readTourStateLocal() || {};
  writeTourStateLocal({ ...current, active: false });
}

function serializeTourState({ persona, selectedIndex, steps, active = true }) {
  const safeSteps = (Array.isArray(steps) ? steps : [])
    .filter(Boolean)
    .map((step) => ({
      id: step.id || "",
      title: step.title || "Шаг",
      description: step.description || "",
      ctaLabel: step.ctaLabel || "Перейти",
      href: step.href || "",
      completed: Boolean(step.completed),
      optional: Boolean(step.optional),
      tourTarget: step.tourTarget || "",
    }));
  const safeIndex = Math.max(0, Math.min(Number(selectedIndex) || 0, Math.max(0, safeSteps.length - 1)));
  return {
    active: Boolean(active),
    persona: persona || "client",
    selectedIndex: safeIndex,
    steps: safeSteps,
  };
}

function fallbackPayload(persona = "client") {
  const isPsy = persona === "psychologist";
  const steps = isPsy
    ? [
        ["profile_completed", "Заполните профиль", "Имя, специализация, описание, формат работы и стоимость.", "Открыть профиль", "/dashboard/settings?tab=psychologist", "[data-onboarding-target='psychologist-profile-fields'], [data-onboarding-target='psychologist-work-fields'], [data-tour='sidebar-profile'], [data-sidebar-key='profile'], [data-sidebar-nav-id='profile']"],
        ["working_hours_set", "Настройте часы", "Укажите доступные дни и время, чтобы клиенты могли записываться без переписок.", "Настроить часы", "/dashboard/calendar", "[data-onboarding-target='calendar-working-hours'], [data-tour='calendar-working-hours'], [data-tour='sidebar-calendar'], [data-sidebar-key='calendar']"],
        ["first_client_added", "Добавьте клиента", "Создайте карточку клиента, чтобы хранить контакты, историю встреч и заметки.", "Добавить клиента", "/dashboard/clients", "[data-tour='sidebar-clients'], [data-sidebar-key='clients']"],
        ["first_session_created", "Запланируйте сессию", "Создайте запись в календаре или отправьте клиенту ссылку для самостоятельной записи.", "Создать запись", "/dashboard/calendar", "[data-onboarding-target='calendar-create-session'], [data-tour='calendar-create-session'], [data-tour='sidebar-calendar'], [data-sidebar-key='calendar']"],
        ["first_note_created", "Создайте первую заметку", "Зафиксируйте наблюдения после встречи — заметки видны только вам.", "Открыть клиентов", "/dashboard/clients", "[data-tour='sidebar-clients'], [data-sidebar-key='clients']"],
        ["first_payment_recorded", "Отметьте первую оплату", "Кошелёк учитывает только реальные оплаты, без отмен и возвратов.", "Открыть финансы", "/dashboard/subscription", "[data-tour='sidebar-subscription'], [data-sidebar-key='subscription']"],
        ["notifications_enabled", "Включите уведомления", "Получайте напоминания о сессиях, новых сообщениях и важных событиях.", "Открыть уведомления", "/notifications", "[data-tour='sidebar-notifications'], [data-sidebar-key='notifications']"],
      ]
    : [
        ["profile_completed", "Заполните профиль", "Добавьте имя и базовую информацию, чтобы специалист видел корректные данные.", "Открыть профиль", "/dashboard/settings?tab=profile", "[data-onboarding-target='profile-basic-fields'], [data-tour='sidebar-profile'], [data-sidebar-key='profile'], [data-sidebar-nav-id='profile']"],
        ["psychologist_selected", "Выберите психолога", "Найдите специалиста и удобное время для первой встречи.", "Найти психолога", "/psychologists", "[data-tour='sidebar-catalog'], [data-sidebar-key='catalog'], [data-tour='sidebar-psychologists']"],
        ["first_session_booked", "Запишитесь на сессию", "После записи встреча появится в календаре и уведомлениях.", "Открыть записи", "/dashboard/appointments", "[data-tour='sidebar-appointments'], [data-sidebar-key='appointments']"],
        ["first_diary_entry_created", "Создайте запись в дневнике", "Отслеживайте состояние между встречами и делитесь важным с психологом.", "Открыть дневник", "/dashboard/diary", "[data-tour='sidebar-diary'], [data-sidebar-key='diary']"],
        ["diary_access_granted", "Поделитесь дневником", "Откройте психологу нужные записи или весь дневник, когда будете готовы.", "Настроить доступ", "/dashboard/diary", "[data-tour='sidebar-diary'], [data-sidebar-key='diary']"],
        ["notifications_enabled", "Включите уведомления", "Получайте напоминания о сессиях и новых сообщениях.", "Открыть уведомления", "/dashboard/notifications", "[data-tour='sidebar-notifications'], [data-sidebar-key='notifications']"],
      ];
  return {
    persona,
    title: isPsy ? "Первый старт практики" : "Первый старт терапии",
    subtitle: isPsy
      ? "Запустите подсказки — система мягко подсветит нужный пункт меню слева и связанную карточку на дашборде."
      : "Запустите подсказки — система поможет заполнить профиль, выбрать специалиста и начать дневник.",
    completedCount: 0,
    totalCount: steps.length,
    progressPercent: 0,
    completed: false,
    dismissed: false,
    shouldShow: true,
    nextStepId: steps[0]?.[0],
    nextStep: null,
    steps: steps.map(([id, title, description, ctaLabel, href, tourTarget]) => ({
      id,
      title,
      description,
      ctaLabel,
      href,
      completed: false,
      optional: id === "first_payment_recorded" || id === "notifications_enabled",
      tourTarget,
    })),
    state: {},
  };
}

function normalizePayload(raw, persona) {
  const src = asObject(raw);
  const fallback = fallbackPayload(persona);
  const fallbackById = new Map(fallback.steps.map((step) => [step.id, step]));
  const apiSteps = Array.isArray(src.steps) ? src.steps.filter(Boolean) : [];
  const apiById = new Map(apiSteps.map((step) => [String(step?.id || ""), step]));
  const fallbackIds = new Set(fallback.steps.map((step) => String(step.id || "")));
  const sourceSteps = [
    ...fallback.steps.map((step) => ({ ...step, ...asObject(apiById.get(String(step.id || ""))) })),
    ...apiSteps.filter((step) => step?.id && !fallbackIds.has(String(step.id))),
  ];
  const steps = sourceSteps.map((step) => {
    const base = fallbackById.get(step?.id) || {};
    const merged = { ...base, ...asObject(step) };
    if (merged.id === "profile_completed") {
      const isPsyProfile = String(src.persona || persona || fallback.persona || "").toLowerCase() === "psychologist";
      return {
        ...merged,
        title: merged.title || "Заполните профиль",
        ctaLabel: "Открыть профиль",
        href: isPsyProfile ? "/dashboard/settings?tab=psychologist" : "/dashboard/settings?tab=profile",
        tourTarget: isPsyProfile
          ? "[data-onboarding-target='psychologist-profile-fields'], [data-onboarding-target='psychologist-work-fields'], [data-tour='sidebar-profile'], [data-sidebar-key='profile'], [data-sidebar-nav-id='profile']"
          : "[data-onboarding-target='profile-basic-fields'], [data-tour='sidebar-profile'], [data-sidebar-key='profile'], [data-sidebar-nav-id='profile']",
      };
    }
    if (merged.id === "working_hours_set") {
      return {
        ...merged,
        href: "/dashboard/calendar",
        ctaLabel: merged.ctaLabel || "Настроить часы",
        tourTarget: "[data-onboarding-target='calendar-working-hours'], [data-tour='calendar-working-hours'], [data-tour='sidebar-calendar'], [data-sidebar-key='calendar'], [data-sidebar-nav-id='calendar']",
      };
    }
    if (merged.id === "first_session_created") {
      return {
        ...merged,
        href: "/dashboard/calendar",
        ctaLabel: merged.ctaLabel || "Создать запись",
        tourTarget: "[data-onboarding-target='calendar-create-session'], [data-tour='calendar-create-session'], [data-tour='sidebar-calendar'], [data-sidebar-key='calendar'], [data-sidebar-nav-id='calendar']",
      };
    }
    if (merged.id === "first_payment_recorded") {
      return {
        ...merged,
        href: "/dashboard/subscription",
        ctaLabel: merged.ctaLabel || "Открыть финансы",
      };
    }
    if (merged.id === "notifications_enabled") {
      return {
        ...merged,
        href: "/notifications",
        ctaLabel: merged.ctaLabel || "Открыть уведомления",
      };
    }
    return {
      ...merged,
      tourTarget: merged.tourTarget || base.tourTarget || "",
      href: merged.href || base.href || "",
      ctaLabel: merged.ctaLabel || base.ctaLabel || "Перейти",
    };
  });
  const next = steps.find((step) => !step.completed && !step.optional) || steps.find((step) => !step.completed) || null;
  const countedSteps = steps.filter((step) => !step.optional);
  const total = countedSteps.length || fallback.totalCount;
  const completed = countedSteps.filter((step) => step.completed).length;
  return {
    ...fallback,
    ...src,
    persona: src.persona || persona || fallback.persona,
    title: fallback.title,
    subtitle: fallback.subtitle,
    totalCount: total,
    completedCount: completed,
    progressPercent: clampProgress(total ? (completed / total) * 100 : 0),
    nextStep: next,
    nextStepId: next?.id || null,
    steps,
  };
}

function normalizePayloadWithLocalProgress(raw, persona) {
  const normalized = normalizePayload(raw, persona);
  const local = readTourStateLocal();
  if (!local || local.persona !== normalized.persona || !Array.isArray(local.steps) || !local.steps.length) {
    return normalized;
  }
  const localById = new Map(local.steps.map((step) => [String(step?.id || ""), step]));
  const mergedSteps = normalized.steps.map((step) => {
    const localStep = localById.get(String(step.id || ""));
    return localStep?.completed ? { ...step, completed: true } : step;
  });
  return normalizePayload({ ...normalized, steps: mergedSteps }, persona);
}

function expandTourSelectors(selector) {
  const base = String(selector || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const extra = [];

  base.forEach((item) => {
    const tourMatch = item.match(/data-tour=['\"]([^'\"]+)['\"]/);
    const sidebarMatch = item.match(/data-sidebar-key=['\"]([^'\"]+)['\"]/);
    const key = tourMatch?.[1] || sidebarMatch?.[1] || "";
    if (!key) return;
    const clean = key.replace(/^sidebar-/, "");
    extra.push(`[data-tour='sidebar-${clean}']`);
    extra.push(`[data-sidebar-key='${clean}']`);
    extra.push(`[data-sidebar-nav-id='${clean}']`);
    if (clean === "psychologists") {
      extra.push("[data-tour='sidebar-catalog']");
      extra.push("[data-sidebar-key='catalog']");
    }
  });

  return Array.from(new Set([...base, ...extra]));
}

function findTarget(selector) {
  if (!selector || typeof document === "undefined") return null;
  const selectors = expandTourSelectors(selector);
  for (const item of selectors) {
    try {
      const node = document.querySelector(item);
      if (node) return node;
    } catch {}
  }
  return null;
}

function getTargetRingStyle(target) {
  if (!target || typeof window === "undefined") return undefined;
  const rect = target.getBoundingClientRect();
  const padX = 8;
  const padY = 7;
  return {
    top: Math.max(8, rect.top - padY),
    left: Math.max(8, rect.left - padX),
    width: Math.min(window.innerWidth - 16, rect.width + padX * 2),
    height: rect.height + padY * 2,
  };
}

function getViewportSafeTop() {
  if (typeof window === "undefined" || typeof document === "undefined") return 14;
  const candidates = [
    ".today-v3__topbar",
    ".podcasts-topbar",
    ".dashboard-topbar",
    ".app-header",
    ".site-header",
    "header[role='banner']",
    "header",
  ];
  let safeTop = 14;
  for (const selector of candidates) {
    const node = document.querySelector(selector);
    if (!node) continue;
    const rect = node.getBoundingClientRect();
    const visibleAtTop = rect.height > 0 && rect.bottom > 0 && rect.top < 140;
    if (!visibleAtTop) continue;
    safeTop = Math.max(safeTop, Math.min(rect.bottom + 14, window.innerHeight - 140));
  }
  return safeTop;
}

function getBubblePosition(target) {
  if (!target || typeof window === "undefined") return undefined;
  const width = Math.min(360, Math.max(300, window.innerWidth - 32));
  const gap = 14;
  const viewportPad = 14;
  const rect = target.getBoundingClientRect();
  const safeTop = getViewportSafeTop();
  const bubbleHeight = 268;
  const placeRight = rect.right + gap + width < window.innerWidth - viewportPad;
  const placeLeft = rect.left - gap - width > viewportPad;
  const left = placeRight
    ? rect.right + gap
    : placeLeft
      ? rect.left - gap - width
      : Math.max(viewportPad, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - viewportPad));
  const maxTop = Math.max(safeTop, window.innerHeight - bubbleHeight - viewportPad);
  const preferredTop = rect.top + rect.height / 2 - bubbleHeight / 2;
  const top = Math.max(safeTop, Math.min(preferredTop, maxTop));
  const targetCenterY = rect.top + rect.height / 2;
  const arrowTop = Math.max(26, Math.min(bubbleHeight - 34, targetCenterY - top - 7));
  return {
    top,
    left,
    width,
    "--tour-arrow-top": `${arrowTop}px`,
    "--tour-safe-top": `${safeTop}px`,
  };
}

function ensureTargetVisible(target) {
  if (!target || typeof window === "undefined") return;
  const rect = target.getBoundingClientRect();
  const inViewport = rect.top >= 8 && rect.bottom <= window.innerHeight - 8 && rect.left >= 0 && rect.right <= window.innerWidth;
  if (inViewport) return;

  const sidebar = target.closest?.(".podcasts-sidebar");
  if (sidebar && typeof target.scrollIntoView === "function") {
    target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    return;
  }

  if (typeof target.scrollIntoView === "function") {
    target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  }
}

function GuidedTourBubble({ step, stepIndex = 0, total = 0, onClose, onPrev, onNext, onAction, onComplete }) {
  const [targetNode, setTargetNode] = React.useState(null);
  const [position, setPosition] = React.useState(undefined);
  const [ringStyle, setRingStyle] = React.useState(undefined);
  const targetRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (!step) return undefined;
    let disposed = false;
    let timeoutOne = 0;
    let timeoutTwo = 0;
    let frameOne = 0;
    let frameTwo = 0;

    const cleanupTarget = () => {
      if (targetRef.current) {
        targetRef.current.classList.remove("tour-target");
        targetRef.current = null;
      }
    };

    const update = () => {
      if (disposed) return;
      const target = findTarget(step.tourTarget);

      if (!target) {
        cleanupTarget();
        setTargetNode(null);
        setPosition(undefined);
        setRingStyle(undefined);
        return;
      }

      if (targetRef.current && targetRef.current !== target) {
        targetRef.current.classList.remove("tour-target");
      }

      targetRef.current = target;
      target.classList.add("tour-target");
      ensureTargetVisible(target);

      const nextPosition = getBubblePosition(target);
      const nextRing = getTargetRingStyle(target);
      setTargetNode(target);
      setPosition(nextPosition);
      setRingStyle(nextRing);
    };

    frameOne = window.requestAnimationFrame(() => {
      update();
      frameTwo = window.requestAnimationFrame(update);
    });
    timeoutOne = window.setTimeout(update, 180);
    timeoutTwo = window.setTimeout(update, 420);

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      disposed = true;
      window.cancelAnimationFrame(frameOne);
      window.cancelAnimationFrame(frameTwo);
      window.clearTimeout(timeoutOne);
      window.clearTimeout(timeoutTwo);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      cleanupTarget();
      setTargetNode(null);
      setPosition(undefined);
      setRingStyle(undefined);
    };
  }, [step]);

  if (!step || !targetNode || !position || !ringStyle) return null;

  return (
    <div className="tour-overlay" aria-label="Подсказка первого старта">
      <span className="tour-soft-ring" style={ringStyle} aria-hidden="true" />
      <div className="tour-bubble show" style={position} aria-live="polite">
        <button type="button" className="tour-bubble-close" onClick={onClose} aria-label="Закрыть подсказку">×</button>
        <div className="tour-bubble-kicker">Подсказка · {stepIndex + 1}/{total || 1}</div>
        <div className="tour-bubble-title">{step.title}</div>
        <div className="tour-bubble-copy">{step.description} Подсвечен нужный элемент на текущей странице или в меню слева.</div>
        <div className="tour-bubble-actions">
          <button type="button" className="tour-bubble-btn" onClick={onPrev} disabled={stepIndex <= 0}>Назад</button>
          {typeof onAction === "function" && step?.href ? (
            <button type="button" className="tour-bubble-btn primary" onClick={onAction}>{step.ctaLabel || "Открыть"}</button>
          ) : (
            <button type="button" className="tour-bubble-btn" onClick={onNext} disabled={stepIndex >= total - 1}>Далее</button>
          )}
          {typeof onComplete === "function" ? (
            <button type="button" className="tour-bubble-btn complete" onClick={onComplete}>Готово</button>
          ) : null}
        </div>
      </div>
    </div>
  );
}


export function GlobalOnboardingTourBridge() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tourState, setTourState] = React.useState(() => readTourStateLocal());

  React.useEffect(() => {
    const sync = () => setTourState(readTourStateLocal());
    sync();
    window.addEventListener(TOUR_STATE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(TOUR_STATE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  React.useEffect(() => {
    setTourState(readTourStateLocal());
  }, [location.pathname, location.search, location.hash]);

  const state = tourState && typeof tourState === "object" ? tourState : null;
  const steps = Array.isArray(state?.steps) ? state.steps : [];
  const selectedIndex = Math.max(0, Math.min(Number(state?.selectedIndex) || 0, Math.max(0, steps.length - 1)));
  const step = steps[selectedIndex] || null;
  const isTodayPage = location.pathname === "/dashboard";

  const updateState = React.useCallback((patch) => {
    const current = readTourStateLocal() || state || {};
    const next = { ...current, ...patch };
    writeTourStateLocal(next);
    setTourState(next);
  }, [state]);

  const move = React.useCallback((delta) => {
    if (!steps.length) return;
    const nextIndex = Math.max(0, Math.min(selectedIndex + delta, steps.length - 1));
    updateState({ selectedIndex: nextIndex, active: true });
  }, [selectedIndex, steps.length, updateState]);

  const stepHref = String(step?.href || "");
  const currentRoute = `${location.pathname || ""}${location.search || ""}`;
  const stepPathOnly = stepHref.split("?")[0];
  const hasStepQuery = stepHref.includes("?");
  const isAlreadyOnStepRoute = Boolean(stepHref) && (hasStepQuery ? currentRoute === stepHref : location.pathname === stepPathOnly);

  const openStep = React.useCallback(() => {
    if (!stepHref) return;
    updateState({ active: true, selectedIndex });
    navigate(stepHref);
  }, [navigate, selectedIndex, stepHref, updateState]);

  const completeStep = React.useCallback(async () => {
    if (!step?.id) return;
    const nextSteps = steps.map((item, index) => index === selectedIndex ? { ...item, completed: true } : item);
    const nextIndex = Math.min(selectedIndex + 1, Math.max(0, nextSteps.length - 1));
    updateState({ steps: nextSteps, selectedIndex: nextIndex, active: true });
    try {
      await onboardingApi.tourComplete(step.id);
    } catch {}
  }, [selectedIndex, step?.id, steps, updateState]);

  const close = React.useCallback(() => {
    stopTourStateLocal();
    setTourState(readTourStateLocal());
  }, []);

  if (!state?.active || !step || isTodayPage) return null;

  return (
    <GuidedTourBubble
      step={step}
      stepIndex={selectedIndex}
      total={steps.length}
      onClose={close}
      onPrev={() => move(-1)}
      onNext={() => move(1)}
      onAction={!isAlreadyOnStepRoute ? openStep : undefined}
      onComplete={completeStep}
    />
  );
}


export default function OnboardingHeroCard({ persona = "client", className = "", compact = false }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [payload, setPayload] = React.useState(() => fallbackPayload(persona));
  const [loading, setLoading] = React.useState(true);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [tourRunning, setTourRunning] = React.useState(false);

  const localKey = `${LOCAL_DISMISS_PREFIX}${persona}`;

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    onboardingApi.get()
      .then((data) => {
        if (cancelled) return;
        setPayload(normalizePayloadWithLocalProgress(data, persona));
      })
      .catch(() => {
        if (cancelled) return;
        setPayload((prev) => normalizePayloadWithLocalProgress(prev, persona));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [persona]);

  const dismissedLocal = (() => {
    try {
      return typeof window !== "undefined" && window.localStorage.getItem(localKey) === "1";
    } catch {
      return false;
    }
  })();

  const normalized = normalizePayload(payload, persona);
  const visibleSteps = normalized.steps || [];
  const nextStep = normalized.nextStep || visibleSteps.find((step) => !step.completed) || null;
  const nextIndex = Math.max(0, visibleSteps.findIndex((step) => step.id === nextStep?.id));
  const shouldShow = !dismissedLocal && normalized.shouldShow !== false && !normalized.completed && !normalized.dismissed;

  React.useEffect(() => {
    if (!tourRunning) setSelectedIndex(nextIndex >= 0 ? nextIndex : 0);
  }, [nextIndex, tourRunning]);

  React.useEffect(() => {
    const stored = readTourStateLocal();
    if (!stored?.active) return;
    if (stored.persona && stored.persona !== persona) return;
    if (!visibleSteps.length && !Array.isArray(stored.steps)) return;
    const max = Math.max(0, (visibleSteps.length || stored.steps?.length || 1) - 1);
    setSelectedIndex(Math.max(0, Math.min(Number(stored.selectedIndex) || 0, max)));
    setTourRunning(true);
  }, [persona, visibleSteps.length]);

  const selectedStep = visibleSteps[selectedIndex] || nextStep || visibleSteps[0] || null;

  React.useEffect(() => {
    if (!visibleSteps.length) return;
    const state = serializeTourState({ persona, selectedIndex, steps: visibleSteps, active: tourRunning });
    if (tourRunning || readTourStateLocal()?.active) {
      writeTourStateLocal(state);
    }
  }, [persona, selectedIndex, tourRunning, visibleSteps]);

  const refresh = React.useCallback(async () => {
    try {
      const data = await onboardingApi.get();
      setPayload(normalizePayloadWithLocalProgress(data, persona));
      toast.success("Стартовые шаги обновлены");
    } catch {
      toast.info("Не удалось обновить шаги. Показываем локальное состояние.");
    }
  }, [persona, toast]);

  const navigateTo = React.useCallback(async (href) => {
    if (!href) return;
    writeTourStateLocal(serializeTourState({
      persona,
      selectedIndex,
      steps: visibleSteps,
      active: true,
    }));
    try {
      await onboardingApi.update({ lastSeenStepId: selectedStep?.id || nextStep?.id || "" });
    } catch {}
    navigate(href);
  }, [navigate, nextStep?.id, persona, selectedIndex, selectedStep?.id, visibleSteps]);

  const dismiss = React.useCallback(async () => {
    setTourRunning(false);
    stopTourStateLocal();
    try {
      const data = await onboardingApi.dismiss();
      setPayload(normalizePayloadWithLocalProgress(data, persona));
    } catch {
      try { window.localStorage.setItem(localKey, "1"); } catch {}
      setPayload((prev) => ({ ...prev, dismissed: true, shouldShow: false }));
    }
    toast.info("Тутор скрыт. Welcome-блок остался на месте.");
  }, [localKey, persona, toast]);

  const restart = React.useCallback(async () => {
    try { window.localStorage.removeItem(localKey); } catch {}
    setTourRunning(false);
    stopTourStateLocal();
    try {
      const data = await onboardingApi.restart();
      setPayload(normalizePayload(data, persona));
    } catch {
      setPayload(fallbackPayload(persona));
    }
    toast.success("Обучение запущено заново");
  }, [localKey, persona, toast]);

  const markStepCompletedLocally = React.useCallback((stepId, { advance = true } = {}) => {
    if (!stepId) return;
    setPayload((prev) => {
      const normalizedPrev = normalizePayload(prev, persona);
      const nextSteps = normalizedPrev.steps.map((step) =>
        step.id === stepId ? { ...step, completed: true } : step
      );
      const nextPayload = normalizePayload({ ...normalizedPrev, steps: nextSteps }, persona);
      writeTourStateLocal(serializeTourState({
        persona,
        selectedIndex: advance
          ? Math.min(selectedIndex + 1, Math.max(0, nextSteps.length - 1))
          : selectedIndex,
        steps: nextSteps,
        active: tourRunning,
      }));
      return nextPayload;
    });
    if (advance) {
      setSelectedIndex((index) => Math.min(index + 1, Math.max(0, visibleSteps.length - 1)));
    }
  }, [persona, selectedIndex, tourRunning, visibleSteps.length]);

  const completeTour = React.useCallback(async () => {
    const id = selectedStep?.id || nextStep?.id || "";
    markStepCompletedLocally(id);
    try {
      const data = await onboardingApi.tourComplete(id);
      const normalizedData = normalizePayload(data, persona);
      const hasServerCompletion = normalizedData.steps.some((step) => step.id === id && step.completed);
      setPayload(hasServerCompletion
        ? normalizedData
        : normalizePayload({
            ...normalizedData,
            steps: normalizedData.steps.map((step) => step.id === id ? { ...step, completed: true } : step),
          }, persona)
      );
      toast.success("Шаг отмечен выполненным");
    } catch {
      toast.info("Шаг отмечен локально. Прогресс обновится после действия в разделе.");
    }
  }, [markStepCompletedLocally, nextStep?.id, persona, selectedStep?.id, toast]);

  const selectStep = React.useCallback((index, { run = true } = {}) => {
    const safe = Math.max(0, Math.min(visibleSteps.length - 1, index));
    setSelectedIndex(safe);
    if (run) setTourRunning(true);
  }, [visibleSteps.length]);

  const moveSelected = React.useCallback((delta) => {
    selectStep(selectedIndex + delta, { run: true });
  }, [selectStep, selectedIndex]);

  if (!shouldShow && !loading) return null;

  const progress = clampProgress(normalized.progressPercent);
  const totalLabel = normalized.totalCount || visibleSteps.length || 0;
  const completedLabel = normalized.completedCount || 0;
  const progressCaption = completedLabel === 0
    ? "готовы начать"
    : completedLabel < Math.max(2, totalLabel - 2)
      ? "идём по плану"
      : completedLabel < totalLabel
        ? "почти готово"
        : "кабинет готов";

  return (
    <section
      className={`tour-card ${tourRunning ? "is-tour-running" : ""} ${compact ? "tour-card--compact" : ""} ${className}`.trim()}
      id="tourCard"
      data-tour="onboarding"
    >
      <div className="tour-head">
        <div>
          <div className="tour-kicker"><span className="tour-kicker-dot" />Временный тутор</div>
          <h2 className="tour-title">{loading ? "Готовим персональный старт" : normalized.title}</h2>
          <p className="tour-copy">{loading ? "Проверяем профиль и рабочие разделы." : normalized.subtitle}</p>
        </div>
        <div className="tour-progress">
          <div className="circle-progress" style={{ "--p": progress }}><strong>{progress}%</strong></div>
          <div className="tour-progress-text"><b>{completedLabel}</b>/{totalLabel} шагов<br /><span>{progressCaption}</span></div>
          <div className="tour-head-actions">
            <button type="button" className="tour-btn tour-btn-green tour-btn-small" id="startTour" onClick={() => setTourRunning((value) => !value)}>
              {tourRunning ? "Остановить" : "Запустить подсказки"}
            </button>
            <button type="button" className="tour-btn tour-btn-secondary tour-btn-small" onClick={dismiss}>Скрыть</button>
          </div>
        </div>
      </div>

      <div className="tour-steps" aria-label="Шаги первого запуска">
        {visibleSteps.map((step, index) => {
          const active = index === selectedIndex;
          const done = Boolean(step.completed);
          return (
            <button
              type="button"
              key={step.id || index}
              className={`tour-step ${active ? "selected" : ""} ${done ? "done" : ""}`}
              data-step={index}
              onClick={() => selectStep(index, { run: true })}
            >
              <div className="step-meta"><div className="step-num">{done ? "✓" : index + 1}</div><div className="step-state">{done ? "Готово" : active && tourRunning ? "Сейчас" : "Шаг"}</div></div>
              <div className="step-title">{step.title}</div>
              <div className="step-desc">{step.description}</div>
              <div className="step-foot"><span>{step.ctaLabel || "Перейти"}</span><span>→</span></div>
            </button>
          );
        })}
      </div>

      {selectedStep ? (
        <div className="tour-detail" id="tourDetail">
          <div className="detail-icon">{selectedStep.completed ? "✓" : selectedIndex + 1}</div>
          <div>
            <div className="detail-label">Шаг {selectedIndex + 1} из {visibleSteps.length}</div>
            <div className="detail-title">{selectedStep.title}</div>
            <div className="detail-copy">
              {selectedStep.description} {tourRunning ? "Подсвечен нужный элемент на текущей странице или в меню." : "Нажмите «Запустить подсказки», чтобы включить мягкую подсветку."}
            </div>
          </div>
          <div className="detail-actions">
            <button type="button" className="tour-btn tour-btn-secondary tour-btn-small" onClick={() => moveSelected(-1)} disabled={selectedIndex <= 0}>Назад</button>
            <button type="button" className="tour-btn tour-btn-secondary tour-btn-small" onClick={() => moveSelected(1)} disabled={selectedIndex >= visibleSteps.length - 1}>Далее</button>
            {selectedStep.href ? (
              <button type="button" className="tour-btn tour-btn-green tour-btn-small" onClick={() => navigateTo(selectedStep.href)}>{selectedStep.ctaLabel || "Открыть"}</button>
            ) : null}
            <button type="button" className="tour-btn tour-btn-gold tour-btn-small" onClick={completeTour}>Готово</button>
          </div>
        </div>
      ) : null}

      <div className="tour-minor-actions">
        <button type="button" onClick={refresh}>Обновить</button>
        <button type="button" onClick={restart}>Начать заново</button>
      </div>

      <GuidedTourBubble
        step={tourRunning ? selectedStep : null}
        stepIndex={selectedIndex}
        total={visibleSteps.length}
        onClose={() => { setTourRunning(false); stopTourStateLocal(); }}
        onPrev={() => moveSelected(-1)}
        onNext={() => moveSelected(1)}
        onAction={() => navigateTo(selectedStep?.href)}
        onComplete={completeTour}
      />
    </section>
  );
}
